/**
 * US-140 Part 4 — an access grant may never exceed the request it came from.
 *
 * `audit_access_requests` records what the auditor asked for (`requested_scopes`,
 * `requested_start_date`, `requested_end_date`). The approval path read **none**
 * of it: window and scopes came straight from the approver's request body, and
 * `access_scopes` defaulted to all five values when omitted — so an approver who
 * clicked through a prefilled form could hand an auditor who asked for
 * `["incidents"]` for one week all PHI for a year, with no warning to either side.
 *
 * That is the HIPAA minimum-necessary standard (§164.502(b)) going unenforced at
 * the one place it can be enforced. These predicates are the enforcement, and they
 * live here — pure, no server deps — so the approval UI can mark a narrowing (and
 * refuse a widening) with the exact rule the backend applies, rather than a
 * client-side re-statement that can drift.
 *
 * An approver may always NARROW. Widening requires a new request.
 */
import type { AuditAccessScope } from "./enums";

/** The bounds a request establishes. Dates are `YYYY-MM-DD`. */
export interface AuditRequestBounds {
  requested_scopes: readonly string[];
  requested_start_date: string;
  requested_end_date: string;
}

/** What the approver proposes to grant. Dates are `YYYY-MM-DD`. */
export interface ProposedGrant {
  access_scopes: readonly string[];
  valid_from: string;
  valid_until: string;
}

export type GrantBoundViolation =
  | { field: "access_scopes"; kind: "not_requested"; offending: string[] }
  | { field: "valid_from"; kind: "before_requested"; limit: string }
  | { field: "valid_until"; kind: "after_requested"; limit: string };

/**
 * A request with no `requested_scopes` predates the field (the column shipped
 * with `@default([])` and nothing ever wrote it). Those rows cannot bound a
 * grant, so the approver's choice stands — but the caller should surface that the
 * request was unscoped rather than pretend it was honoured.
 */
export function isUnscopedRequest(request: AuditRequestBounds): boolean {
  return request.requested_scopes.length === 0;
}

/**
 * Every way `proposed` exceeds `request`. Empty array = within bounds.
 *
 * Dates are compared as `YYYY-MM-DD` strings — lexicographic order is
 * chronological for that format, which sidesteps the timezone bugs that
 * `new Date(dateOnly)` introduces (it parses as UTC midnight, then any local
 * -offset formatting shifts the day).
 */
export function checkGrantWithinRequest(
  request: AuditRequestBounds,
  proposed: ProposedGrant,
): GrantBoundViolation[] {
  const violations: GrantBoundViolation[] = [];

  if (!isUnscopedRequest(request)) {
    const allowed = new Set(request.requested_scopes);
    const offending = proposed.access_scopes.filter((s) => !allowed.has(s));
    if (offending.length > 0) {
      violations.push({
        field: "access_scopes",
        kind: "not_requested",
        offending,
      });
    }
  }

  if (proposed.valid_from < request.requested_start_date) {
    violations.push({
      field: "valid_from",
      kind: "before_requested",
      limit: request.requested_start_date,
    });
  }
  if (proposed.valid_until > request.requested_end_date) {
    violations.push({
      field: "valid_until",
      kind: "after_requested",
      limit: request.requested_end_date,
    });
  }

  return violations;
}

/** Field-keyed messages, in the shape the API's validation errors use. */
export function describeGrantViolations(
  violations: readonly GrantBoundViolation[],
): Record<string, string[]> {
  const details: Record<string, string[]> = {};
  for (const v of violations) {
    const message =
      v.kind === "not_requested"
        ? `Not requested by the auditor: ${v.offending.join(", ")}. An approval may narrow the request, never widen it.`
        : v.kind === "before_requested"
          ? `Cannot start before the requested start date (${v.limit}).`
          : `Cannot end after the requested end date (${v.limit}).`;
    (details[v.field] ??= []).push(message);
  }
  return details;
}

/**
 * Which requested scopes the approver is withholding — for the approval UI, so a
 * narrowing is visible rather than silent. Returns [] when nothing is withheld.
 */
export function withheldScopes(
  request: AuditRequestBounds,
  proposed: ProposedGrant,
): AuditAccessScope[] {
  const granted = new Set(proposed.access_scopes);
  return request.requested_scopes.filter(
    (s) => !granted.has(s),
  ) as AuditAccessScope[];
}
